import { Signal } from '@angular/core';

import { z } from 'zod';
import { IDatiqRulesData, IDatiqRulesRule } from './iq-rules.models';

/**
 * Generates a code snippet for a single CSS class and selectors that don't require rules.
 * This is to be used if you want these CSS classes to ALWAYS show.
 *
 * @param className - CSS Class you want to add to various elements
 * @param cssSelectors - An array of selectors you want to apply to the class
 * @returns A record of the class you want to add with the selectors and an empty array of rules
 */
export const singleClasses = (
    className: string,
    cssSelectors: string[],
): IqCssClassnames => ({
    [className]: [
        {
            rules: [],
            data: cssSelectors,
        },
    ],
});
/**
 * Generates a code snippet for a single CSS class and selectors that require rules.
 *
 * @param className - CSS Class you want to add to various elements
 * @param cssSelectors - An array of selectors you want to apply to the class
 * @param rules - An array of selectors you want to apply to the class
 * @returns A record of the class you want to add with the selectors and an empty array of rules
 */
export const singleClassesWithRules = (
    className: string,
    cssSelectors: string[],
    rules: IDatiqRulesRule[],
): IqCssClassnames => ({
    [className]: [
        {
            rules,
            data: cssSelectors,
        },
    ],
});
/**
 * Generates a code snippet for a single CSS class and selectors that require rules.
 *
 * @param classNames- CSS Class you want to add to various elements
 * @param cssSelectors - An array of selectors you want to apply to the class
 * @param rules - An array of selectors you want to apply to the class
 * @returns A record of the class you want to add with the selectors and an empty array of rules
 */
export const multipleClassesWithSameRules = (
    classNames: string[],
    cssSelectors: string[],
    rules: IDatiqRulesRule[],
): IqCssClassnames => {
    return classNames.reduce((currentClasses: IqCssClassnames, className) => {
        return {
            ...currentClasses,
            ...singleClassesWithRules(className, cssSelectors, rules),
        };
    }, {} as IqCssClassnames);
};
/**
 * Generates a code snippet for CSS classes and selectors that don't require rules.
 * This is to be used if you want these CSS classes to ALWAYS show.
 *
 * @param classNames - CSS Classnames you want to add to various elements
 * @param cssSelectors - An array of selectors you want to apply to the class
 * @returns A record of the class you want to add with the selectors and an empty array of rules
 */
export const multiClasses = (
    classNames: string[],
    cssSelectors: string[],
): IqCssClassnames => {
    return classNames.reduce((currentClasses: IqCssClassnames, className) => {
        return {
            ...currentClasses,
            ...singleClasses(className, cssSelectors),
        };
    }, {} as IqCssClassnames);
};

/**
 * Since containers don't require a selector, this creates a record
 * that contains the classnames and the rules to apply to the container.
 *
 * @param classNames Classnames to add to a container
 * @param rules Rules to determine if you want to apply them
 * @returns Record of the classnames for the container
 */
export const addContainerClassesRules = (
    classNames: string[],
    rules: IDatiqRulesData<string[]>[],
): IqCssClassnames => {
    return (
        classNames.reduce((currentClasses: IqCssClassnames, className) => {
            return {
                ...currentClasses,
                [className]: [...(rules ?? [])],
            };
        }, {} as IqCssClassnames) ?? {}
    );
};

/**
 * Permantently adds classes to a container with no rules.
 *
 * @param classNames classes to add to the container
 * @returns Record of classes to add to the container with no rules
 */
export const addContainerClassesNoRules = (
    classNames: string[],
): IqCssClassnames => {
    return addContainerClassesRules(classNames, [{ rules: [], data: [''] }]);
};

export const IqStyleProcessorStateServiceSchema = z.object({});

export interface IIqStyleProcessorStateService {
    appliedClasses(classes: IqCssClassnames): Signal<[string, string[]][]>;
    show(rules: IDatiqRulesRule[]): Signal<boolean>;
}

/**
 * Generates a code snippet for shared labels with the correct classes.
 *
 * @param icon - icon to add to your label
 * @param label - the label to add along side the icon
 * @param filled - sets the icon to a filled state
 * @param opts - various options for the label
 * @param opts.classes - classes to add to the label
 * @returns markup for an icon and label
 */
export const mdIconLabel = (
    icon: string,
    label: string = '',
    filled: boolean = false,
    opts: { classes: string[] } = { classes: [] },
): string => {
    const classes = opts.classes.join(' ');
    const filledClass = filled ? 'iq-mat-icon-fill' : '';
    return !!label?.length
        ? `<span data-uitype='md-icon' class='material-symbols-outlined iq-md-icon-label-icon ${filledClass} ${classes}'>${icon}</span><span class='iq-md-icon-label-text ${classes}'>${label}</span>`
        : `<span data-uitype='md-icon' class='material-symbols-outlined iq-md-icon-label-icon ${filledClass} ${classes}'>${icon}</span>`;
};
/**
 * Generates markdown for a checked checkbox label.
 *
 * @param label - the label to add along side the icon
 * @param icon - icon to add to your label
 * @param filled - the icon for label (default true since most of the use cases will be checkbox filled)
 * @param opts - various options for the label
 * @param opts.classes - classes to add to the label
 * @returns markup for an icon and label
 */
export const checkBoxLabelChecked = (
    label: string,
    icon: string = 'check_box',
    filled: boolean = true,
    opts: { classes: string[] } = { classes: [] },
): string => {
    const classes = opts.classes.join(' ');
    const filledClass = filled ? 'iq-mat-icon-fill' : '';

    return `<span data-uitype='md-icon' class='material-symbols-outlined iq-md-icon-label-icon text-dat-blue-500 ${classes} ${filledClass}'>${icon}</span> <span class='iq-md-icon-label-text'>${label}</span>`;
};
/**
 * Generates markdown for a unchecked checkbox label.
 *
 * @param label - the label to add along side the icon
 * @param icon - icon to add to your label (default check_box_outline_blank)
 * @param filled - the icon for label (default false since most of the use cases will be checkbox unfilled)
 * @param opts - various options for the label
 * @param opts.classes - classes to add to the label
 * @returns markup for an icon and label
 */
export const checkBoxLabelUnchecked = (
    label: string,
    icon: string = 'check_box_outline_blank',
    filled: boolean = false,
    opts: { classes: string[] } = { classes: [] },
): string => {
    const classes = opts.classes.join(' ');
    const filledClass = filled ? 'iq-mat-icon-fill' : '';

    return `<span data-uitype='md-icon' class='material-symbols-outlined iq-md-icon-label-icon ${classes} ${filledClass}'>${icon}</span> <span class='iq-md-icon-label-text'>${label}</span>`;
};
/**
 * Generates markdown for a disabled checkbox label.
 *
 * @param label - the label to add along side the icon
 * @param icon - icon to add to your label (default check_box_outline_blank)
 * @param filled - the icon for label (default false since most of the use cases will be checkbox unfilled)
 * @param opts - various options for the label
 * @param opts.classes - classes to add to the label
 * @returns markup for an icon and label
 */
export const checkBoxLabelDisabled = (
    label: string,
    icon: string = 'check_box_outline_blank',
    filled: boolean = false,
    opts: { classes: string[] } = { classes: [] },
): string => {
    const classes = opts.classes.join(' ');
    const filledClass = filled ? 'iq-mat-icon-fill' : '';

    return `<span data-uitype='md-icon' class="material-symbols-outlined ${classes} ${filledClass}">${icon}</span> <span class="iq-md-icon-label-text">${label}</span>`;
};

export type IqCssClassnames = Record<string, IDatiqRulesData<string[]>[]>;

/**
 * Generates a string to targtet elements by ui type attribute
 * @param uiType - the name of the ui-type to match
 * @returns ui type selector string
 */
export const createUiTypeSelectorString = (uiType: string): string => {
    return `[data-uitype="${uiType}"]`;
};

/**
 * Generates a string to targtet elements by style target attriute
 * @param styleTarget - the name of the style target to match
 * @returns style target selector string
 */
export const createStyleTargetSelectorString = (
    styleTarget: string,
): string => {
    return `[data-styletarget="${styleTarget}"]`;
};
/**
 * Generates a string to targtet elements by style target attriute
 * @param styleId - the name of the style target to match
 * @returns style target selector string
 */
export const createStyleIdSelectorString = (
    styleId: string,
): string => {
    return `[data-styleid="${styleId}"]`;
};

/**
 * Generates a classnames record that will hide the subscript wrapper in a material input
 * @returns IqCssClassnames record that hides the subscript wrapper
 */
export const matInputHideSubscript = (): IqCssClassnames => {
    return multiClasses(['hidden'], ['.mat-mdc-form-field-subscript-wrapper']);
};

/**
 * Used to set classes to a specific selector. The rules are optional
 * so we can make the classes always show if none appear.
 */
export type IqCssClassNamesRule = {
    classNames: string[];
    cssSelectors: string[];
    rules?: {
        [key: string]: any;
        type: string;
    }[];
    ruleOperator?: string;
};
